
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader } from 'lucide-react';
import { toast } from 'sonner';

interface AuthProtectedRouteProps {
  children: React.ReactNode;
}

// Paths a customer-role account may visit; everything else bounces to the library.
const CUSTOMER_ALLOWED_PREFIXES = ['/library', '/profile', '/logout', '/change-password'];

const AuthProtectedRoute: React.FC<AuthProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, session, userProfile, forceRefreshSession, debugMode } = useAuth();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [authVerified, setAuthVerified] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  
  // Enhanced auth verification with multiple checks
  useEffect(() => {
    let isMounted = true;
    
    const verifyAuth = async () => {
      if (!isMounted) return;
      
      // If already verified or too many attempts, stop trying
      if (authVerified || verificationAttempts >= 3) {
        setIsVerifying(false);
        return;
      }
      
      if (debugMode) {
        console.log(`AuthProtectedRoute: Verifying authentication (attempt ${verificationAttempts + 1})`, {
          path: location.pathname,
          isAuthenticated,
          hasSession: !!session,
          hasToken: !!session?.access_token,
          isLoading
        });
      }
      
      // If already authenticated with valid session, mark as verified
      if (isAuthenticated && session?.access_token) {
        if (debugMode) console.log('AuthProtectedRoute: Authentication verified');
        setAuthVerified(true);
        setIsVerifying(false);
        return;
      }
      
      // If not loading and still not authenticated, try one forced refresh
      if (!isLoading && !isAuthenticated && verificationAttempts < 2) {
        try {
          if (debugMode) console.log('AuthProtectedRoute: Forcing session refresh');
          const refreshed = await forceRefreshSession();
          
          if (refreshed && isMounted) {
            if (debugMode) console.log('AuthProtectedRoute: Session refreshed successfully');
            // Increment attempts but continue verification in next cycle
            setVerificationAttempts(prev => prev + 1);
            return;
          }
        } catch (error) {
          console.error('Auth verification error:', error);
        }
      }
      
      // If we still don't have auth after attempts, stop verifying
      if (verificationAttempts >= 2 || (!isLoading && !isAuthenticated)) {
        if (debugMode) console.log('AuthProtectedRoute: Authentication failed after attempts');
        setIsVerifying(false);
      }
    };
    
    // Start verification if not already verified
    if (!authVerified) {
      verifyAuth();
    }
    
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading, session, location, authVerified, verificationAttempts, forceRefreshSession, debugMode]);
  
  // Show loading indicator while verifying or loading auth state
  if (isLoading || isVerifying) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="relative">
            <Loader size={40} className="text-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-primary" />
            </div>
          </div>
          <p className="text-muted-foreground animate-pulse">Verifying authentication...</p>
        </div>
      </div>
    );
  }
  
  // If verified and authenticated, render the children
  if (authVerified && isAuthenticated) {
    // Customers only see the library and their profile — the dashboard is for
    // admin/user roles. Wait for the profile so we don't redirect mid-load.
    if (
      userProfile?.role === 'customer' &&
      !CUSTOMER_ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))
    ) {
      return <Navigate to="/library" replace />;
    }
    return <>{children}</>;
  }
  
  // If not authenticated after verification, redirect to login
  toast.error('Please sign in to access this page');
  return <Navigate to="/" state={{ from: location }} replace />;
};

export default AuthProtectedRoute;
